/**
 * PayrollRecordPanel — the RECORD action on the pay-run screen: the
 * preview-then-commit gate over POST /v1/payroll/periods/preview (PR 172) and
 * POST /v1/payroll/periods (PR 170).
 *
 * WHY THE GATE IS THE WHOLE FEATURE: a recorded period is PERMANENT. There is
 * no supersession write path yet and no DELETE grant, so a wrong click cannot
 * be taken back — it can only be superseded by a slice that does not exist.
 * The operator therefore commits nothing they have not first seen.
 *
 * EVERY FIGURE ON THIS PANEL COMES FROM THE SERVER. The preview response is
 * the row the commit would store, computed by the identical transaction rolled
 * back instead of committed (same employee lock, same in-tx profile resolve,
 * same Σ over current prior rows, same engine calls). Nothing here is derived
 * in the browser — not the period number, not the tax year, not one penny.
 * The browser could not reproduce them faithfully even if it tried, and a
 * preview that disagreed with the write would be worse than no preview.
 *
 * INVALIDATE ON EDIT (the anti-drift rule): any change to any input discards
 * the preview, which disables the commit until the operator previews again. A
 * stale preview must never be committable — the figures on screen would then
 * describe inputs that are no longer the ones about to be sent.
 *
 * IDEMPOTENCY KEY, ONE PER PREVIEW: a crypto.randomUUID() minted when a
 * preview succeeds and carried on every commit attempt for that preview. A
 * double-click, a retry after a network error, or a re-click after an
 * ambiguous failure all send the SAME key, so the server answers with the
 * first write's row (200) instead of writing a second one. The key dies with
 * the preview it belongs to: a new preview is a new intent and mints a new key.
 *
 * Page-shape lineage: the inputs + confirm idiom is CreateExpenseModal
 * (pages/finance/FinanceExpenses.tsx), but rendered INLINE in a ClosrCard
 * rather than a portal modal — the gate's whole job is to be compared against
 * the pay-run table beneath it, and an Escape-dismissable overlay would put a
 * two-step flow one stray keypress from losing its previewed state.
 */
import { useState } from 'react'
import { Check, Eye, X } from 'lucide-react'
import { previewPayrollPeriod, recordPayrollPeriod, resolvePayrollProfile, useAsync } from '../../api'
import type {
  ApiEmployeeDirectoryRow, PayrollPeriodResult, RecordPayrollPeriodPreview, RecordPayrollPeriodRequest,
} from '../../types'
import { apiErrorMessage } from '../../../../api/client'
import { money, signedMoney, penceCell } from '../../../../lib/money'
import { DeductionList, DeductionRowsEditor } from './DeductionFields'
import {
  deductionsTotalPence, draftsToDeductions, draftsValid, type DeductionDraft,
} from './deductions'
import { HoursList, HoursSection } from './HoursFields'
import { hoursBodyFor, hoursDraftsValid, type HoursAnswer, type HoursDraft } from './hours'

const inputCls = 'w-full h-10 px-3 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent'
const labelCls = 'block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1'

const BASIS_LABEL: Record<string, string> = {
  cumulative: 'Cumulative', week1_month1: 'W1/M1',
}

/** A successful preview and the commit key minted for it. They live and die
 *  together: dropping this state is exactly what invalidate-on-edit means. */
type Previewed = {
  preview: RecordPayrollPeriodPreview
  idempotencyKey: string
}

/** One labelled figure in the preview grid. */
function Figure({ label, value, muted = false }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted">{label}</div>
      <div className={`text-13 tabular-nums ${muted ? 'text-ink-secondary' : 'text-ink-primary font-medium'}`}>{value}</div>
    </div>
  )
}

export default function PayrollRecordPanel({ employees, onRecorded, onClose }: {
  employees: ApiEmployeeDirectoryRow[]
  /** Called with the freshly written row so the page can refresh the pay run
   *  and move to the period the SERVER assigned it. */
  onRecorded: (row: PayrollPeriodResult) => void
  onClose: () => void
}) {
  const [employeeId, setEmployeeId] = useState('')
  const [regularPayday, setRegularPayday] = useState('')
  const [paymentDate, setPaymentDate] = useState('')
  const [gross, setGross] = useState('')
  const [changeReason, setChangeReason] = useState('')
  // Deductions start EMPTY on a record: a fresh period has nothing to inherit,
  // so there is no tri-state here — omitted and [] both mean "none", and this
  // panel simply omits the field when the list is empty.
  const [deductions, setDeductions] = useState<DeductionDraft[]>([])
  // ── s.8(2)(e) ────────────────────────────────────────────────────────────
  // WHICH PROFILE IS IN FORCE depends on the payday, so the flag is resolved
  // per payday rather than per employee. `flagShown` is the CLIENT's guess
  // from the profile history — it decides only what to SHOW. The PREVIEW is
  // authoritative and decides the REQUIREMENT; when a preview lands its
  // payVariesByReferenceToTimeWorked overrides this, which is how a stale
  // history (a profile edited in another tab) self-corrects.
  const [hoursAnswer, setHoursAnswer] = useState<HoursAnswer>('unanswered')
  const [hoursRows, setHoursRows] = useState<HoursDraft[]>([])

  // THE CLIENT'S RESOLUTION, and it is deliberately the SERVER'S resolver
  // rather than window arithmetic in the browser: GET .../profile?on=<payday>
  // asks the same question the write path asks, so the only way the two can
  // disagree is a profile edited between this call and the preview. It
  // decides what to SHOW.
  const { data: inForce } = useAsync(
    () => (employeeId !== '' && regularPayday !== ''
      ? resolvePayrollProfile(employeeId, regularPayday)
      : Promise.resolve(null)),
    [employeeId, regularPayday],
  )

  const [previewed, setPreviewed] = useState<Previewed | null>(null)
  // THE SERVER'S RESOLUTION FROM THE PREVIEW WINS when one exists — it is the
  // value the write path actually judged against, so a stale client guess
  // self-corrects the moment a preview lands.
  const flagTrue = previewed
    ? previewed.preview.payVariesByReferenceToTimeWorked
    : (inForce?.payVariesByReferenceToTimeWorked ?? false)
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [committed, setCommitted] = useState<PayrollPeriodResult | null>(null)

  // THE ANTI-DRIFT RULE, in one place: every input change routes through here,
  // so no field can be edited without discarding the preview (and with it the
  // commit key — a new intent gets a new key). Wrapping the setters rather
  // than diffing a snapshot keeps the invariant local to the edit itself.
  function edit<T>(setter: (v: T) => void): (v: T) => void {
    return (v: T) => {
      setPreviewed(null)
      setCommitted(null)
      setError(null)
      setter(v)
    }
  }

  const grossNum = Number(gross)
  const grossPence = Math.round(grossNum * 100)
  const inputsValid =
    employeeId !== '' &&
    regularPayday !== '' &&
    gross !== '' && Number.isFinite(grossNum) && grossNum >= 0 &&
    // Required in the UI because the server requires it — surfacing a 400 for
    // something the form can see is missing would be the worse gate.
    changeReason.trim() !== '' &&
    // A half-filled deduction row blocks the preview for the same reason: the
    // server would 400 on a missing purpose or an unparseable amount, and the
    // form can see it.
    draftsValid(deductions) &&
    // The either/or: for a time-varying worker submit stays shut until the
    // operator has answered, because the server answers silence with a named
    // 400 and a form that could send silence would be manufacturing it.
    (!flagTrue || (hoursAnswer !== 'unanswered' && (hoursAnswer === 'none' || (hoursRows.length > 0 && hoursDraftsValid(hoursRows)))))

  function requestBody(): RecordPayrollPeriodRequest {
    return {
      employeeId,
      regularPayday,
      ...(paymentDate !== '' ? { paymentDate } : {}),
      grossPence,
      changeReason: changeReason.trim(),
      // Omitted entirely when there are none — on THIS route omitted and []
      // mean the same thing, so the smaller body is the honest one.
      ...(deductions.length > 0 ? { deductions: draftsToDeductions(deductions) } : {}),
      // OMITTED entirely for a non-time-varying worker: the server refuses a
      // supplied list BY NAME, and [] counts as supplied. For a time-varying
      // one this is [] or the rows — never absent, which is the whole point.
      ...(() => {
        const h = hoursBodyFor(flagTrue, hoursAnswer, hoursRows)
        return h === undefined ? {} : { hours: h }
      })(),
    }
  }

  async function runPreview() {
    setBusy('preview')
    setError(null)
    try {
      const preview = await previewPayrollPeriod(requestBody())
      // One key per successful preview — see the header comment.
      setPreviewed({ preview, idempotencyKey: crypto.randomUUID() })
    } catch (e) {
      setPreviewed(null)
      setError(apiErrorMessage(e, 'Previewing this period failed'))
    } finally {
      setBusy(null)
    }
  }

  async function commit() {
    if (!previewed || previewed.preview.alreadyRecorded) return
    setBusy('commit')
    setError(null)
    try {
      const row = await recordPayrollPeriod(requestBody(), previewed.idempotencyKey)
      setCommitted(row)
      onRecorded(row)
    } catch (e) {
      setError(apiErrorMessage(e, 'Recording this period failed'))
    } finally {
      setBusy(null)
    }
  }

  const row = previewed?.preview.row
  const alreadyRecorded = previewed?.preview.alreadyRecorded === true
  const commitDisabled = !previewed || alreadyRecorded || busy !== null || committed !== null

  return (
    <div className="k-card">
      <div className="p-4 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">Record a period</div>
            <div className="text-12 text-ink-muted mt-0.5 leading-relaxed">
              Preview first: the figures below are computed by the payroll engine and are exactly
              what will be stored. A recorded period cannot be edited or deleted yet.
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* ── Step 1: inputs ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label htmlFor="rec-employee" className={labelCls}>Employee</label>
            <select id="rec-employee" value={employeeId} onChange={e => edit(setEmployeeId)(e.target.value)} className={inputCls}>
              <option value="" disabled>Select…</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.employeeNumber} — {e.fullName}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="rec-payday" className={labelCls}>Regular payday</label>
            <input id="rec-payday" type="date" value={regularPayday}
              onChange={e => edit(setRegularPayday)(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="rec-payment" className={labelCls}>
              Payment date <span className="font-normal normal-case tracking-normal text-ink-faint">(optional)</span>
            </label>
            <input id="rec-payment" type="date" value={paymentDate}
              onChange={e => edit(setPaymentDate)(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="rec-gross" className={labelCls}>Gross (£)</label>
            <input id="rec-gross" type="number" min={0} step={0.01} value={gross}
              onChange={e => edit(setGross)(e.target.value)} placeholder="2000.00" className={inputCls} />
          </div>
          <div>
            <label htmlFor="rec-reason" className={labelCls}>Change reason</label>
            <input id="rec-reason" value={changeReason}
              onChange={e => edit(setChangeReason)(e.target.value)}
              placeholder="September pay run" className={inputCls} />
          </div>
        </div>

        {/* ── Deductions ─────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-2 pt-3 border-t border-edge-row-faint">
          <div>
            <div className="k-eyebrow uppercase">Deductions</div>
            <div className="text-12 text-ink-muted mt-0.5 leading-relaxed">
              Non-statutory deductions taken from net pay. Each needs a purpose — it is the
              particular that gives the figure meaning on a payslip. Leave empty if there are none.
            </div>
          </div>
          {/* Routed through edit() exactly like every other input: a deduction
              row is an input, and changing one must discard a standing preview
              or the committed figures would describe a list nobody previewed. */}
          <DeductionRowsEditor idPrefix="rec-ded" drafts={deductions}
            onChange={edit(setDeductions)} disabled={busy !== null} />
        </div>

        {/* ── s.8(2)(e) hours — CONDITIONAL ON THE PAYDAY'S DECLARATION ───
            ABSENT, not disabled, when the worker's pay does not vary by time.
            A greyed-out hours section would read as "optional", and supplying
            hours for such a worker earns a named refusal from the server —
            so the honest surface is no surface. */}
        {flagTrue && (
          <div className="flex flex-col gap-2 pt-3 border-t border-edge-row-faint">
            <HoursSection idPrefix="rec-hrs" answer={hoursAnswer}
              onAnswer={a => { edit(setHoursAnswer)(a) }}
              drafts={hoursRows} onChange={edit(setHoursRows)} disabled={busy !== null} />
          </div>
        )}

        {/* The period number and tax year are NOT inputs: the server derives
            them from the regular payday (CWG2 §1.8) and the preview reports
            what it chose. Saying so is the honest alternative to a field the
            operator would expect to control. */}
        <div className="text-12 text-ink-muted leading-relaxed">
          The tax year and period number are derived from the regular payday — the preview reports
          which period this payment falls in.
        </div>

        <div className="flex items-center gap-2">
          <button onClick={runPreview} disabled={!inputsValid || busy !== null}
            className="k-btn-secondary k-btn-secondary--flex disabled:opacity-40 disabled:cursor-not-allowed">
            <Eye size={14} /> {busy === 'preview' ? 'Previewing…' : 'Preview'}
          </button>
          <button onClick={commit} disabled={commitDisabled}
            className="k-btn k-btn--lg inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <Check size={14} /> {busy === 'commit' ? 'Recording…' : 'Record this period'}
          </button>
          {!previewed && !error && (
            <span className="text-12 text-ink-muted">Preview before recording.</span>
          )}
        </div>

        {error && (
          <div className="text-13 text-danger leading-relaxed" role="alert">{error}</div>
        )}

        {committed && (
          <div className="text-13 text-ink-primary leading-relaxed">
            Recorded — {money(committed.grossPence / 100)} for tax year {committed.taxYear},
            period {committed.periodNumber}.
          </div>
        )}

        {/* ── Step 2: the previewed row, entirely server-computed ─────────── */}
        {row && (
          <div className="flex flex-col gap-3 pt-3 border-t border-edge-row-faint">
            {alreadyRecorded ? (
              <div className="text-13 text-ink-primary leading-relaxed">
                <span className="font-semibold">Already recorded.</span>{' '}
                Tax year {row.taxYear}, period {row.periodNumber} already has a row for this
                employee — shown below as stored. Recording it again cannot change it.
              </div>
            ) : (
              <div className="text-12 text-ink-muted leading-relaxed">
                This is what will be stored for tax year {row.taxYear}, period {row.periodNumber}.
              </div>
            )}

            <div className="grid grid-cols-4 gap-x-4 gap-y-3">
              <Figure label="Period" value={`${row.periodNumber} · ${row.taxYear}`} />
              <Figure label="Gross" value={signedMoney(row.grossPence)} />
              <Figure label="Employee NI" value={penceCell(row.niEmployeePence)} />
              <Figure label="Employer NI" value={penceCell(row.niEmployerPence)} />
              <Figure label="PAYE tax" value={penceCell(row.payeTaxPence)} />
              <Figure label="Student loan" value={penceCell(row.studentLoanPence)} />
              <Figure label="PGL" value={penceCell(row.postgraduateLoanPence)} />
              <Figure label="Tax code" value={row.taxCode ?? '—'} muted />
              <Figure label="Basis" value={BASIS_LABEL[row.taxBasis] ?? row.taxBasis} muted />
              <Figure label="Frequency" value={row.payFrequency} muted />
              {/* The priors the engine actually consumed — absent on an
                  already-recorded preview, because nothing was recomputed. */}
              <Figure label="Prior pay to date"
                value={previewed?.preview.derivedPriorPayPence === undefined ? '—' : signedMoney(previewed.preview.derivedPriorPayPence)} muted />
              <Figure label="Prior tax to date"
                value={previewed?.preview.derivedPriorTaxPence === undefined ? '—' : signedMoney(previewed.preview.derivedPriorTaxPence)} muted />
              {/* The total that would be subtracted, summed from the SERVER'S
                  resolved list — not from the form state.

                  THERE IS NO NET FIGURE HERE, deliberately. netPence lives on
                  PayrollLedgerRow (the two ledger READ surfaces); the write
                  surface returns PayrollPeriodResult, on which NetPence is a Go
                  METHOD and not a wire field, so the preview response carries
                  no net at all. Computing one in the browser would break this
                  panel's own rule that every figure comes from the server, and
                  would be the fabrication the data-honesty rule forbids — the
                  browser cannot reproduce the engine faithfully, and a net that
                  disagreed with the stored row would be worse than none. The
                  recorded row's net appears on the pay-run table the moment
                  this commits, where the server does ship it. */}
              <Figure label="Deductions" value={money(deductionsTotalPence(previewed?.preview.deductions ?? []) / 100)} />
            </div>

            {/* The itemisation the write would store, beside the figures it
                moved. Rendered from the PREVIEW's list, never from the form
                state: what matters is what the server resolved. */}
            <div className="flex flex-col gap-1 pt-3 border-t border-edge-row-faint">
              <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted">
                Deductions to be stored
              </div>
              <DeductionList deductions={previewed?.preview.deductions ?? []} />
            </div>

            {/* The RESOLVED hours, from the server's list rather than the
                form state — absent entirely when the duty does not attach,
                which is the same absent-versus-empty distinction the wire
                makes. */}
            {previewed?.preview.hours !== undefined && (
              <div className="flex flex-col gap-1 pt-3 border-t border-edge-row-faint">
                <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted">
                  Hours to be stored
                </div>
                <HoursList hours={previewed.preview.hours} />
              </div>
            )}

            <div className="text-11 text-ink-muted leading-relaxed break-all">
              Profile version {row.profileId} · engine {previewed?.preview.engineIdentity}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
